import "./AdminNavbar.css";
import Modal from "react-modal";
import crssrImage from "../../images/singleinvoice/cross-red.svg";
import { Link } from 'react-router-dom';
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import { useSelector, useDispatch } from "react-redux";
import Notification from "../Notification/Notification";
import MobileGlobalSearch from "./MobileGlobalSearch";
import AIChat from "../AIChat/AIChat";
import AIUserChat from "../AIChat/AIUserChat";
import React, { useState, useEffect, useRef, useCallback } from "react";
import user_profile from "../../images/user/Default_pfp.png";
import { logout, reset } from "../../features/auth/authSlice";
import { setHeaderText } from "../../features/headerText/headerTextSlice";
import { reset as invoiceReset, setDashboardDate, setInvoiceType } from "../../features/invoice/invoiceSlice.js";
import { reset as headerReset } from "../../features/headerText/headerTextSlice.js";
import { reset as appSettingsReset } from "../../features/appSettings/appSettingsSlice.js";
import { resetAccountDetails } from "../../features/accountDetails/accountDetailsSlice.js";
import Pusher from 'pusher-js';
import userCategory from '../../constants/userCategory.js';
import userTypeConsts from '../../constants/userTypeConsts.js';
import InactivityHandler from "./InactivityHandler.jsx";
import Sidebar from "../Sidebar/Sidebar.jsx";
import getPusherConfig from "../../config/pusherConfig.js";
import { setSecureStorage, removeSecureStorage } from "../../utils/secureStorage.js";
import { getBrandLogo, getBrandLogoAlt, isMvpEnv } from "../../utils/brandLogo";
import { setSideBarText } from "../../features/sideBarText/sideBarTextSlice.js";

const AdminNavbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth);
  const [isOpen, setIsOpen] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [dashboardText, setDashboardText] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [isAIChatOpen, setIsAIChatOpen] = useState(false);
  const [isAIUserChatOpen, setIsAIUserChatOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const userSectionRef = useRef(null);
  const headerText = useSelector(
    (state) => state.headerText.headerTextState
  );
  const { appSettings } = useSelector((state) => state.appSettings);
  const userId = useSelector((state) => state.auth.user?._id);

  // Centralized logout handler
  const handleLogout = useCallback((shouldReload = false) => {
    try {
      // Use secure storage utility for safe localStorage operations
      removeSecureStorage("user");
      dispatch(logout());
      dispatch(reset());
      dispatch(invoiceReset());
      dispatch(headerReset());
      dispatch(appSettingsReset());
      document.body.classList.remove('open-menu-mob');

      if (shouldReload) {
        window.location.reload();
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error('Logout error:', error);
      window.location.href = "/";
    }
  }, [dispatch, navigate]);
  // Check if current month is December using timezone from global.config
  const getCurrentMonth = () => {
    try {
      const timezone = global.config && global.config.getTimeZone ? global.config.getTimeZone() : 'UTC';
      return moment().tz(timezone).month(); // 0-indexed, so 11 = December
    } catch (error) {
      // Fallback to local timezone if there's an error
      return new Date().getMonth();
    }
  };
  const isDecember = getCurrentMonth() === 11;
  const currentLogo = getBrandLogo({ christmas: isDecember });
  const isMvp = isMvpEnv();
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    setIsActive(!isActive);
    dispatch(resetAccountDetails());
  };

  const profileSetting = () => {
    dispatch(setHeaderText("Profile Settings"));
    // Use secure storage utility for safe localStorage operations
    setSecureStorage('activeTab', '0');
    setIsOpen(false);
  };

  const pusherInstanceRef = useRef(null);

  useEffect(() => {
    if (!userId) return;

    // Cleanup any existing Pusher instance before creating a new one
    if (pusherInstanceRef.current) {
      try {
        const existingInstance = pusherInstanceRef.current;
        if (existingInstance.connection && existingInstance.connection.state !== 'disconnected' && existingInstance.connection.state !== 'disconnecting') {
          existingInstance.disconnect();
        }
      } catch (error) {
        console.debug('Pusher cleanup error (safe to ignore):', error);
      }
      pusherInstanceRef.current = null;
    }

    const initializePusher = async () => {
      try {
        // Get Pusher configuration from environment variables
        const config = getPusherConfig();

        if (!config) {
          console.warn('Pusher is not available due to missing configuration. Please set REACT_APP_PUSHER_KEY in your environment variables.');
          return;
        }

        // Initialize Pusher with configuration
        const pusherInstance = new Pusher(config.key, {
          cluster: config.cluster,
          encrypted: config.encrypted,
        });

        // Store instance in ref for cleanup
        pusherInstanceRef.current = pusherInstance;

        // Handle connection errors
        pusherInstance.connection.bind('error', (err) => {
          console.error('Pusher connection error:', err);
        });

        // Subscribe to the user-specific channel and event
        const channel = pusherInstance.subscribe(userId);

        channel.bind('pusher:subscription_error', (status) => {
          console.error('Pusher subscription error:', status);
        });

        channel.bind('userLogout', () => {
          handleLogout(true); // Reload on logout
        });

        channel.bind('userRefresh', () => {
          window.location.reload();
        });

        // Subscribe to the 'allUsers' channel for global events
        const channel2 = pusherInstance.subscribe('allUsers');

        channel2.bind('pusher:subscription_error', (status) => {
          console.error('Pusher allUsers subscription error:', status);
        });

        channel2.bind('forceLogout', () => {
          handleLogout(true); // Reload on logout
        });

        channel2.bind('forceReload', () => {
          window.location.reload();
        });

      } catch (error) {
        console.error('Failed to initialize Pusher:', error);
        // Don't break the app if Pusher fails - just log the error
      }
    };

    initializePusher();

    // Cleanup on unmount
    return () => {
      const pusherInstance = pusherInstanceRef.current;
      if (pusherInstance) {
        try {
          // Check if connection exists and is actually connected
          if (pusherInstance.connection) {
            const state = pusherInstance.connection.state;
            // Only disconnect if connection is fully established
            // Avoid disconnecting when 'connecting' to prevent WebSocket errors
            if (state === 'connected') {
        pusherInstance.disconnect();
            }
            // If connecting, just unbind events and let it fail naturally
            else if (state === 'connecting') {
              // Unbind all event handlers to prevent memory leaks
              pusherInstance.connection.unbind_all();
            }
          }
        } catch (error) {
          // Silently handle disconnect errors (connection might already be closed)
          // This is safe to ignore as the connection will be cleaned up anyway
        } finally {
          pusherInstanceRef.current = null;
        }
      }
    };
  }, [userId, handleLogout]);  // Dependencies to re-run effect when `userId` or `handleLogout` change

  const handleBodyClick = useCallback((event) => {
    if (userSectionRef.current && !userSectionRef.current.contains(event.target)) {
      // Use setTimeout to ensure the updates are non-blocking
      setTimeout(() => {
        setIsOpen(false);
        setIsActive(false);
        document.body.classList.remove("open-menu-mob");
      }, 0); // 0 ms ensures it happens after current event handling
    }
  }, []);

  useEffect(() => {
    document.body.addEventListener("click", handleBodyClick);

    // Cleanup the event listener on unmount
    return () => {
      document.body.removeEventListener("click", handleBodyClick);
    };
  }, [handleBodyClick]);

  useEffect(() => {
    if (isActive) {
      document.body.classList.add("open-menu-mob");
    } else {
      document.body.classList.remove("open-menu-mob");
    }
  }, [isActive]);

  const handleCloseSideBar = () => {
    setIsOpen(false);
  };

  const handleAIChatToggle = useCallback(() => {
    setIsAIChatOpen(prev => !prev);
  }, []);
  const handleAIUserChatToggle = useCallback(() => {
    setIsAIUserChatOpen(prev => !prev);
  }, []);

  const handleCloseAIChat = useCallback(() => {
    setIsAIChatOpen(false);
  }, []);
  const handleCloseAIUserChat = useCallback(() => {
    setIsAIUserChatOpen(false);
  }, []);

  useEffect(() => {
    if (headerText) {
      setDashboardText(headerText);
    }
    setProfileImage(user.user?.profile_image);
  }, [headerText, user]);

  // Check if current hostname is allowed for AI chat access
  const isAllowedHostname = () => {
    const hostname = window.location.hostname;
    // Get allowed hostnames from environment variable or use defaults
    const allowedHostnames = process.env.REACT_APP_AI_CHAT_HOSTNAMES
      ? process.env.REACT_APP_AI_CHAT_HOSTNAMES.split(',')
      : ['test.localhost', '127.0.0.1', 'test.app.talenton.io', 'limenzy.app.talenton.io','wx-agency.talenton.app'];

    return allowedHostnames.includes(hostname);
  };

  // Check if user has admin/super admin role and is on allowed hostname
  const canAccessAIChat = (user.user?.userType === userTypeConsts.SUPER_ADMIN ||
    user.user?.userType === userTypeConsts.ADMIN) &&
    isAllowedHostname();

  // Check if user is a normal user (not admin or super admin)
  const canAccessUserChat = (user.user?.userType !== userTypeConsts.SUPER_ADMIN &&
    user.user?.userType !== userTypeConsts.ADMIN) &&
    isAllowedHostname();

  const isVendor = user.user?.userCategory === userCategory.Vendor;
  const isClient = user.user?.userCategory === userCategory.Client;
  const dashboardPath = isVendor
    ? "/vendor/dashboard"
    : isClient
      ? "/client/dashboard"
      : "/user/dashboard";

  const handleLogoClick = () => {
    dispatch(resetAccountDetails());
    dispatch(setSideBarText(""));
    dispatch(setInvoiceType(null));
    dispatch(
      setDashboardDate([
        new Date(
          moment()
            .tz(global.config?.getTimeZone?.() || "UTC")
            .subtract(30, "years")
            .startOf("year")
            .format()
        ).toISOString(),
        new Date(
          moment()
            .tz(global.config?.getTimeZone?.() || "UTC")
            .endOf("year")
            .format()
        ).toISOString(),
      ])
    );
  };

  return (
    <div className='navbar-outer'>
      <div className='navbar-inner'>
        <div className='navbar-lf'>
          <h1 className='logo-main-nav-text-m11'>{dashboardText}</h1>
          <Link
            to={dashboardPath}
            className="mobile-nav-logo-link"
            onClick={handleLogoClick}
            aria-label="Go to dashboard"
          >
            <img
              className={`image-for-mob-only-nav1${isMvp ? " logo-mvp" : ""}`}
              src={currentLogo}
              alt={getBrandLogoAlt()}
            />
          </Link>
        </div>

        <div className="navbar-rt">
          {/* Mobile search button */}
          <div className="nav-search-mob">
            <button
              className="mob-icon-btn"
              aria-label="Search"
              type="button"
              onClick={() => setIsMobileSearchOpen(true)}
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="9" cy="9" r="6.5" stroke="#6A7988" strokeWidth="1.8" />
                <path d="M14 14L18 18" stroke="#6A7988" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <MobileGlobalSearch
            isOpen={isMobileSearchOpen}
            onClose={() => setIsMobileSearchOpen(false)}
          />

          {canAccessAIChat && (
            <div className="nav-ai">
              <button className="ai-button" onClick={handleAIChatToggle}>
                <div className="ai-button-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 38 38" fill="none">
                    <path d="M18.75 0.25C29.1053 0.25 37.5 8.64466 37.5 19C37.5 29.3553 29.1053 37.75 18.75 37.75C8.39466 37.75 0 29.3553 0 19C0 8.64466 8.39466 0.25 18.75 0.25ZM18.75 1.75C9.22309 1.75 1.5 9.47309 1.5 19C1.5 28.5269 9.22309 36.25 18.75 36.25C28.2769 36.25 36 28.5269 36 19C36 9.47309 28.2769 1.75 18.75 1.75ZM21.2451 15.5977V21.4385H21.2441V21.4619L21.2236 21.4492L16.5537 18.582V18.5752H16.5527V18.5811L16.5537 18.582V24.2256L21.2236 21.4492L21.2451 21.4629V21.4385L26.0625 18.5752V24.252L16.5537 30.0908V24.2344L10.875 27.7197V21.9482L16.5469 18.5801L10.875 15.0967V9.4375L21.2451 15.5977ZM24.9121 8.50879C24.9685 9.55791 25.5008 11.5736 28.1055 12.3789L28.0752 12.8643C27.5169 12.9634 26.7187 13.3231 26.0625 13.9697C25.4525 14.5709 24.97 15.4144 24.9121 16.5244L24.9062 16.75H24.4062C24.4062 16.0149 24.0176 15.0965 23.374 14.3252C22.7319 13.5558 21.8709 12.975 20.9697 12.8662L21 12.3682C21.4814 12.3682 22.3379 12.0439 23.0801 11.3535C23.8115 10.6731 24.4062 9.65971 24.4062 8.3125H24.9062L24.9121 8.50879Z" fill="url(#paint0_linear_2781_2507)" />
                    <defs>
                      <linearGradient id="paint0_linear_2781_2507" x1="10.4167" y1="8.0625" x2="35.4167" y2="37.75" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#975EFF" />
                        <stop offset="1" stopColor="#5DC8FA" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <span className="ai-button-text">assistant</span>
              </button>
            </div>
          )}

          {/* user chat - only accessible for normal users (not admins/super admins) */}
          {canAccessUserChat && (
            <div className="nav-ai">
              <button className="ai-button" onClick={handleAIUserChatToggle}>
                <div className="ai-button-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 38 38" fill="none">
                    <path d="M18.75 0.25C29.1053 0.25 37.5 8.64466 37.5 19C37.5 29.3553 29.1053 37.75 18.75 37.75C8.39466 37.75 0 29.3553 0 19C0 8.64466 8.39466 0.25 18.75 0.25ZM18.75 1.75C9.22309 1.75 1.5 9.47309 1.5 19C1.5 28.5269 9.22309 36.25 18.75 36.25C28.2769 36.25 36 28.5269 36 19C36 9.47309 28.2769 1.75 18.75 1.75ZM21.2451 15.5977V21.4385H21.2441V21.4619L21.2236 21.4492L16.5537 18.582V18.5752H16.5527V18.5811L16.5537 18.582V24.2256L21.2236 21.4492L21.2451 21.4629V21.4385L26.0625 18.5752V24.252L16.5537 30.0908V24.2344L10.875 27.7197V21.9482L16.5469 18.5801L10.875 15.0967V9.4375L21.2451 15.5977ZM24.9121 8.50879C24.9685 9.55791 25.5008 11.5736 28.1055 12.3789L28.0752 12.8643C27.5169 12.9634 26.7187 13.3231 26.0625 13.9697C25.4525 14.5709 24.97 15.4144 24.9121 16.5244L24.9062 16.75H24.4062C24.4062 16.0149 24.0176 15.0965 23.374 14.3252C22.7319 13.5558 21.8709 12.975 20.9697 12.8662L21 12.3682C21.4814 12.3682 22.3379 12.0439 23.0801 11.3535C23.8115 10.6731 24.4062 9.65971 24.4062 8.3125H24.9062L24.9121 8.50879Z" fill="url(#paint0_linear_2781_2507)" />
                    <defs>
                      <linearGradient id="paint0_linear_2781_2507" x1="10.4167" y1="8.0625" x2="35.4167" y2="37.75" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#975EFF" />
                        <stop offset="1" stopColor="#5DC8FA" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <span className="ai-button-text">assistant</span>
              </button>
            </div>
          )}


          {user.user?.is_notification_enabled === true && (user && (user.user?.userCategory !== userCategory.Vendor) && (user.user?.userCategory !== userCategory.Client)) && (
            <div className="nav-noti">
              <Notification />
            </div>
          )}
          <div className={`user-sec ${isActive ? "active" : ""}`} ref={userSectionRef}>
            <div className="user-inner">
              <div className="user-img" onClick={toggleDropdown}>
                {user.isLoading ? (
                  <img src={user_profile} alt="User" />
                ) : (
                  <>
                    {user.user && user.user.profile_image && user.user.profile_image !== "null" ? (
                      <>
                        {profileImage ? (<img
                          src={
                            user.user.userCategory === userCategory.Vendor
                              ? `/uploads/vendorCompyLogo/orgiQuality/${profileImage}`
                              : user.user.userCategory === userCategory.Client
                                ? `/uploads/clientCompyLogo/orgiQuality/${profileImage}`
                                : `/uploads/orgiProfileImage/${profileImage}`
                          }
                          alt="User"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/uploads/lowProfileImage/user_default.png';
                          }}
                        />) : "Loading.."}
                      </>
                    ) : (
                      <img src={user_profile} alt="User" />
                    )}
                  </>
                )}
              </div>
              {isOpen && (
                <div className="inner-profile inner-profile-enter">
                  <div className='mobile-menu-new-outer'>
                    <div className="mobile-menu-new">
                      <Sidebar onCancel={handleCloseSideBar} />
                    </div>
                    <div className='sidebar-mob-sec'>
                      <Link to="/user/profile/settings" className={headerText === "Profile Settings" ? "active" : ""} onClick={profileSetting}>
                        <span className='icon-menu'>
                          <svg xmlns="http://www.w3.org/2000/svg" width="23" height="22" viewBox="0 0 23 22" fill="none">
                            <path d="M16.5334 5.00821L21.3308 5.0021C21.7028 5.00162 22.0047 4.6973 22.0042 4.32331C22.0038 3.94932 21.7011 3.64576 21.3291 3.64624L16.5317 3.65235C16.2162 2.10709 14.8527 0.942822 13.2244 0.944897C11.5962 0.946971 10.2354 2.11471 9.92413 3.66077L1.55107 3.67144C1.1791 3.67191 0.87719 3.97623 0.877666 4.35023C0.878143 4.72422 1.18083 5.02777 1.5528 5.0273L9.92701 5.01663C10.2425 6.56189 11.606 7.72615 13.2342 7.72408C14.8625 7.722 16.2232 6.55427 16.5334 5.00821ZM11.2069 4.33706C11.2055 3.21512 12.1112 2.30218 13.2271 2.30076C14.343 2.29933 15.251 3.20996 15.2524 4.33191C15.2539 5.45386 14.3482 6.3668 13.2323 6.36822C12.1164 6.36964 11.2083 5.45901 11.2069 4.33706ZM10.4082 11.6189L21.3391 11.605C21.711 11.6045 22.0129 11.3002 22.0125 10.9262C22.012 10.5522 21.7093 10.2487 21.3373 10.2492L10.4065 10.2631C10.091 8.71782 8.72749 7.55356 7.09925 7.55563C5.4709 7.55771 4.11026 8.72544 3.79894 10.2715L1.55926 10.2744C1.18728 10.2748 0.885373 10.5792 0.88585 10.9531C0.886326 11.3271 1.18901 11.6307 1.56099 11.6302L3.80179 11.6274C4.11728 13.1726 5.48078 14.3369 7.10901 14.3348C8.73621 14.3327 10.0969 13.165 10.4082 11.6189ZM5.08173 10.9478C5.0803 9.82585 5.986 8.91291 7.1019 8.91149C8.2178 8.91007 9.12583 9.8207 9.12726 10.9426C9.12869 12.0646 8.22298 12.9775 7.10708 12.979C5.99118 12.9804 5.08316 12.0697 5.08173 10.9478ZM19.1717 18.2107L21.3473 18.2079C21.7193 18.2075 22.0212 17.9031 22.0207 17.5291C22.0203 17.1552 21.7176 16.8516 21.3456 16.8521L19.17 16.8548C18.8545 15.3096 17.491 14.1453 15.8628 14.1474C14.2345 14.1495 12.8738 15.3172 12.5624 16.8633L1.56744 16.8773C1.19546 16.8777 0.893553 17.1821 0.89403 17.5561C0.894506 17.9301 1.19719 18.2336 1.56917 18.2331L12.5653 18.2191C12.8808 19.7644 14.2443 20.9287 15.8726 20.9266C17.4998 20.9245 18.8604 19.7556 19.1717 18.2107ZM13.8452 17.5396C13.8438 16.4176 14.7495 15.5047 15.8654 15.5033C16.9813 15.5018 17.8893 16.4125 17.8908 17.5344C17.8922 18.6564 16.9865 19.5693 15.8706 19.5707C14.7547 19.5721 13.8467 18.6604 13.8452 17.5396Z" fill="#6A7988" stroke="white" strokeWidth="0.3" />
                          </svg>
                        </span>
                        {global.config.locate(appSettings && appSettings.language, 'Profile_Settings')}
                      </Link>
                      <Link
                        onClick={(e) => {
                          e.preventDefault();
                          setIsOpen(false);
                          setIsActive(false);
                          setIsLogoutModalOpen(true);
                        }}
                      >
                        <span className='icon-menu'>
                          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                            <path d="M22 11.0002C22 17.0651 17.0649 22 10.9998 22C6.77443 22 2.87425 19.5352 1.05905 15.7148C0.939043 15.4645 1.04874 15.1645 1.29437 15.0502C1.5447 14.9302 1.83908 15.0398 1.95908 15.2855C3.60916 18.7552 7.15964 21.0004 10.9998 21.0004C16.5153 21.0004 21.0006 16.5152 21.0006 11C21.0006 5.48476 16.5153 0.999614 10.9998 0.999614C7.15964 0.999614 3.60916 3.24495 1.95908 6.71453C1.83908 6.96485 1.54374 7.06985 1.29437 6.94985C1.04404 6.82985 0.939035 6.53452 1.05905 6.28516C2.87413 2.46482 6.77419 0 10.9998 0C17.0649 0 22 4.93491 22 10.9998V11.0002ZM12.0247 15.0251C11.8297 15.2201 11.8297 15.5351 12.0247 15.7301C12.125 15.8304 12.2497 15.8754 12.38 15.8754C12.5103 15.8754 12.635 15.8257 12.7353 15.7301L17.1155 11.35C17.3105 11.155 17.3105 10.84 17.1155 10.645L12.7353 6.26502C12.5403 6.07001 12.2253 6.07001 12.0303 6.26502C11.8353 6.46002 11.8353 6.77502 12.0303 6.97002L15.5556 10.4952H0.499703C0.22501 10.4952 0 10.7202 0 10.9948C0 11.2695 0.22501 11.4945 0.499703 11.4945H15.5556L12.0247 15.0251Z" fill="#6A7988" />
                          </svg>
                        </span>
                        {global.config.locate(appSettings && appSettings.language, 'Logout')}
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <InactivityHandler />
      <AIChat isOpen={isAIChatOpen} onClose={handleCloseAIChat} />
      <AIUserChat isOpen={isAIUserChatOpen} onClose={handleCloseAIUserChat} />
      <Modal
        className="logout-confirm-modal"
        overlayClassName="logout-confirm-overlay"
        isOpen={isLogoutModalOpen}
        onRequestClose={() => setIsLogoutModalOpen(false)}
        closeTimeoutMS={480}
        contentLabel="Log Out"
      >
        <div className="modal">
          <div className="modal-close-invite outter-modal-main">
            <a onClick={() => setIsLogoutModalOpen(false)} style={{ cursor: "pointer" }}>
              <img src={crssrImage} alt="Close" />
            </a>
          </div>
          <div className="modal-client-head">
            <h2>Log Out</h2>
          </div>
          <div className="form-field-client-outer">
            <p style={{ color: "#78828D", fontSize: "15px", marginBottom: "28px", lineHeight: "1.5", textAlign: "center" }}>
              Are you sure you want to log out of your account?
            </p>
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => setIsLogoutModalOpen(false)}
                style={{
                  flex: 1,
                  padding: "12px 20px",
                  borderRadius: "6px",
                  border: "1px solid #d0d5dd",
                  background: "#fff",
                  color: "#282828",
                  fontSize: "15px",
                  cursor: "pointer",
                  fontWeight: "500",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => { setIsLogoutModalOpen(false); handleLogout(false); }}
                style={{
                  flex: 1,
                  padding: "12px 20px",
                  borderRadius: "6px",
                  border: "none",
                  background: "#6479F8",
                  color: "#fff",
                  fontSize: "15px",
                  cursor: "pointer",
                  fontWeight: "500",
                }}
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>

  );
};

export default AdminNavbar;